import { useEffect, useState } from "react";
import { CardItemProps } from "../../../components/CardSelect/CardSelect";
import { billingInformationsState } from "../../billingInformations/billingInformationsSlice";
import { CountryDisplay } from "../../billingInformations/models/CountryDisplay.interface";
import { Plan } from "../models/Plan.interface";
import { computeVat, fetchPlans, planUpgradeState } from "../planUpgradeSlice";
import { computeAutoPay, extractDuration, humanizeBytes } from "../../../shared/helpers/helpers";
import { useCountry } from "../../../hooks/country/useCountry";
import { useDiscovery } from "../../../hooks/discovery/useDiscovery";
import { useInformation } from "../../billingInformations/hooks/useInformation";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux/hooks";
import { smashPriceformatter } from "../../../components/SmashPrice/SmashPrice";
import { ComputeVatOutput } from "@smash-sdk/vat/10-2019";
import { GetPlanOutput } from "@smash-sdk/billing/01-2022";

const filterPlansByCurrency = (plans: Plan[], currency: string) => {
    return plans.filter(plan => plan.currency === currency);
}

const filterPlansByCountry = (plans: Plan[], countries: CountryDisplay[], countryCode: string) => {
    const currentCountry = countries.find((country) => country.countryCode === countryCode);
    return plans.filter(plan => plan.currency === currentCountry?.currency);
}

const formatPlans = (plans: Plan[], selectedPlan: Plan | null): CardItemProps[] => {
    const formattedPlans = plans.map(plan => {
        const priceFormated = smashPriceformatter(plan.priceExclTax, plan.currency);
        const { unit } = extractDuration(plan.duration);
        const selected = selectedPlan ? !!(selectedPlan && selectedPlan.id === plan.id) : false;
        return { id: plan.id, title: humanizeBytes(plan.limits.services.transfer.dataInOut.max) as string, subTitle: `${priceFormated}/${unit}`, selected };
    });
    return formattedPlans;
}

export const usePlanForSelect = (): [boolean, CardItemProps[], (currency: string) => void] => {
    const dispatch = useAppDispatch();
    const [isCountrySuccess, countries] = useCountry();
    const [discoveryIsLoading, discoveryIsSuccess, userCurrentCountryCode] = useDiscovery();
    const [informationIsLoading, userInformations] = useInformation();

    const [plans, setFormattedPlans] = useState<CardItemProps[]>([]);
    const [currency, setCurrency] = useState<string | undefined>();
    const { availablePlans, selectedPlan } = useAppSelector(planUpgradeState);

    useEffect(() => {
        if (!availablePlans.isLoading && !availablePlans.isSuccess) {
            dispatch(fetchPlans());
        }
        if (availablePlans.plans && countries && userCurrentCountryCode && !currency) { // this is to show plans based on user location
            const targetCountryCode = userInformations ? userInformations.countryCode : userCurrentCountryCode;
            const filteredPlans = filterPlansByCountry(availablePlans.plans, countries, targetCountryCode);
            const formattedPlans = formatPlans(filteredPlans, selectedPlan.plan);
            setFormattedPlans(formattedPlans);
        }
        if (selectedPlan.isSuccess && currency) { // show plans when user select
            const filteredPlans = filterPlansByCurrency(availablePlans.plans, currency);
            const formattedPlans = formatPlans(filteredPlans, selectedPlan.plan);
            setFormattedPlans(formattedPlans);
        }
    }, [availablePlans, userInformations, userCurrentCountryCode, countries, selectedPlan, dispatch]);

    const filterByCurrency = (currency: string) => {
        setCurrency(currency);
        const filtered = availablePlans.plans.filter((plan: Plan) => plan.currency === currency);
        // dispatch(resetSelectedPlan()); // That can help
        const formattedPlans = formatPlans(filtered, selectedPlan.plan);
        setFormattedPlans(formattedPlans);
    }

    return [availablePlans.isLoading, plans, filterByCurrency];
}

interface ComputedPlan {
    planName: string;
    priceExclTax: number;
    priceInclTax: number;
    currency: 'eur' | 'usd';
    percent: string;
    vat: number;
    total: number;
    autoPayOn: string;
}

function formatPlanSummary(plan: GetPlanOutput['plan'], vat: ComputeVatOutput['vat']): ComputedPlan { // FIXME Types ?
    return {
        planName: plan.name,
        priceExclTax: vat.priceExclTax,
        priceInclTax: vat.priceInclTax,
        currency: plan.currency,
        percent: vat.percent + '%',
        vat: vat.vat,
        total: vat.priceInclTax,
        autoPayOn: computeAutoPay(plan.duration),
    }
}

export const usePlanForSummary = (): [boolean, ComputedPlan | undefined] => {
    const dispatch = useAppDispatch();
    const [planSummary, setComputedPlan] = useState<ComputedPlan | undefined>();
    const { vat, selectedPlan } = useAppSelector(planUpgradeState);
    const { billingInformation } = useAppSelector(billingInformationsState);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    useEffect(() => {
        setIsLoading(true);
        if (!billingInformation.isLoading && billingInformation.informations && selectedPlan.plan && !vat.isLoading && !vat.isSuccess) {
            const payload = {
                vatNumber: billingInformation.informations.vatNumber,
                priceExclTax: selectedPlan.plan.priceExclTax,
                countryCode: billingInformation.informations.countryCode as string,
                bypassVatCheck: true,
            }
            dispatch(computeVat(payload));
        }

        if (selectedPlan.plan && vat.isSuccess && vat.vat) {
            const formattedPlan = formatPlanSummary(selectedPlan.plan, vat.vat);
            setComputedPlan(formattedPlan);
            setIsLoading(false);
        }

    }, [vat, billingInformation, selectedPlan, dispatch]);

    return [isLoading, planSummary];
}
